import { useCallback, useEffect, useMemo, useState } from "react";

import axios from "axios";
import { useFlags } from "launchdarkly-react-client-sdk";
import { mockGetLiferayData } from "tests/mocks/mockGetLiferayData";
import { mockGetTier } from "tests/mocks/mockGetTier";

import { SESSION_TOKEN } from "../constants";
import { TGetLiferayDataResponse, TGetTierResponse } from "./types";
import { getCategoryBenefitsDataResponse, getTierResponse } from "./mappers";

const contentType = "application/json";

export const useVoeBizApi = () => {
  const ldFlags = useFlags();
  const { apiDomain } = ldFlags;
  const vbzDebug = localStorage.getItem("vbzDebug");

  const [cmsDomain, setCmsDomain] = useState("");

  const handleGetIsClub = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem(SESSION_TOKEN);
    const url = `${apiDomain}/corporate-club/v1/active-flag`;

    if (vbzDebug) {
      return true;
    }

    const result = await axios.get<{
      isClubCorporateActive: boolean;
    }>(url, {
      headers: {
        "Content-Type": contentType,
        Authorization: `Bearer ${token}`,
      },
    });

    return result.data.isClubCorporateActive;
  }, [apiDomain, vbzDebug]);

  const handleGetTier = useCallback(async (): Promise<string | undefined> => {
    const token = localStorage.getItem(SESSION_TOKEN);
    const url = `${apiDomain}/members/v1/members/tier`;

    if (vbzDebug) {
      return getTierResponse(mockGetTier);
    }

    const response = await axios.get<TGetTierResponse>(url, {
      headers: {
        "Content-Type": contentType,
        Authorization: `Bearer ${token}`,
      },
    });

    return getTierResponse(response.data);
  }, [apiDomain, vbzDebug]);

  const handleGetCategoryBenefitsData = useCallback(
    (liferayDomain: string, siteId: string) => async () => {
      const url = `${liferayDomain}/o/headless-delivery/v1.0/sites/${siteId}/structured-contents?flatten=true&filter=friendlyUrlPath eq 'vbz_category_benefits'`;
      if (vbzDebug) {
        return getCategoryBenefitsDataResponse({ data: mockGetLiferayData });
      }

      return axios.get<TGetLiferayDataResponse>(url).then(getCategoryBenefitsDataResponse);
    },
    [vbzDebug],
  );

  useEffect(() => {
    if (ldFlags.apiDomain) {
      let liferayDomain = "";
      if (window.location.hostname === "localhost") {
        liferayDomain = ldFlags.apiDomain.replace("apigw-voebiz", "voebiz").replace("-hml5", ".dev1");
        setCmsDomain(liferayDomain);
      }
    }
  }, [ldFlags.apiDomain]);

  const VBZAPI = useMemo(() => {
    return {
      getTier: handleGetTier,
      getIsClub: handleGetIsClub,
      getCategoryBenefitsData: handleGetCategoryBenefitsData(cmsDomain, ldFlags.liferaySiteId),
    };
  }, [cmsDomain, handleGetCategoryBenefitsData, handleGetIsClub, handleGetTier, ldFlags.liferaySiteId]);

  return VBZAPI;
};
